/**
 * Design System - Tooltip Component
 *
 * Accessible tooltip with multiple positions and animations.
 *
 * @example
 * <Tooltip content="This is a tooltip">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * @example
 * <Tooltip
 *   content="Save your changes"
 *   position="right"
 *   variant="dark"
 * >
 *   <IconButton icon={<FiSave />} />
 * </Tooltip>
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { cn } from '../utils/classNames';

const Tooltip = ({
  children,
  content,
  position = 'top',
  variant = 'dark',
  delay = 200,
  offset = 8,
  disabled = false,
  className,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  // Variant styles
  const variantStyles = {
    dark: 'bg-gray-900 text-white',
    light: 'bg-white text-gray-900 border border-gray-200 shadow-lg',
  };

  // Position arrow styles
  const arrowStyles = {
    top: {
      tooltip: 'bottom-full left-1/2 -translate-x-1/2',
      arrow: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent',
      arrowColor: variant === 'dark' ? 'border-t-gray-900' : 'border-t-white',
    },
    bottom: {
      tooltip: 'top-full left-1/2 -translate-x-1/2',
      arrow: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent',
      arrowColor: variant === 'dark' ? 'border-b-gray-900' : 'border-b-white',
    },
    left: {
      tooltip: 'right-full top-1/2 -translate-y-1/2',
      arrow: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent',
      arrowColor: variant === 'dark' ? 'border-r-gray-900' : 'border-r-white',
    },
    right: {
      tooltip: 'left-full top-1/2 -translate-y-1/2',
      arrow: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent',
      arrowColor: variant === 'dark' ? 'border-l-gray-900' : 'border-l-white',
    },
  };

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + offset;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - offset;
        break;
      case 'right':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + offset;
        break;
      default:
        break;
    }

    // Keep tooltip within viewport
    const padding = 8;
    if (left < padding) left = padding;
    if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltipRect.height > window.innerHeight - padding) {
      top = window.innerHeight - tooltipRect.height - padding;
    }

    setCoords({ top, left });
  };

  const showTooltip = () => {
    if (disabled) return;
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);

      return () => {
        window.removeEventListener('scroll', calculatePosition, true);
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isVisible]);

  // Clone child with event handlers
  const trigger = React.cloneElement(React.Children.only(children), {
    ref: triggerRef,
    onMouseEnter: (e) => {
      showTooltip();
      children.props.onMouseEnter?.(e);
    },
    onMouseLeave: (e) => {
      hideTooltip();
      children.props.onMouseLeave?.(e);
    },
    onFocus: (e) => {
      showTooltip();
      children.props.onFocus?.(e);
    },
    onBlur: (e) => {
      hideTooltip();
      children.props.onBlur?.(e);
    },
    'aria-describedby': isVisible ? 'tooltip' : undefined,
  });

  const tooltipContent = isVisible && content && (
    <div
      ref={tooltipRef}
      id="tooltip"
      role="tooltip"
      style={{
        position: 'fixed',
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        zIndex: 9999,
      }}
      className={cn(
        'px-3 py-2 text-sm rounded-lg pointer-events-none animate-fade-in',
        variantStyles[variant],
        className
      )}
    >
      {content}
      {/* Arrow */}
      <div
        className={cn(
          'absolute w-0 h-0 border-4',
          arrowStyles[position].arrowColor
        )}
        style={{
          ...(position === 'top' && { top: '100%', left: '50%', transform: 'translateX(-50%)' }),
          ...(position === 'bottom' && { bottom: '100%', left: '50%', transform: 'translateX(-50%)' }),
          ...(position === 'left' && { left: '100%', top: '50%', transform: 'translateY(-50%)' }),
          ...(position === 'right' && { right: '100%', top: '50%', transform: 'translateY(-50%)' }),
        }}
      />
    </div>
  );

  return (
    <>
      {trigger}
      {tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  );
};

Tooltip.propTypes = {
  children: PropTypes.element.isRequired,
  content: PropTypes.node.isRequired,
  position: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  variant: PropTypes.oneOf(['dark', 'light']),
  delay: PropTypes.number,
  offset: PropTypes.number,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default Tooltip;
